import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import type { RootState } from "../store";
import { OfferCategory } from "../../types/offers";

interface OffersState {
  // Filters
  filters: {
    category: OfferCategory | "all";
    sortBy: "newest" | "expiring" | "discount" | "popular";
    searchQuery: string;
  };
}

const initialState: OffersState = {
  filters: {
    category: "all",
    sortBy: "newest",
    searchQuery: "",
  },
};

const offersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    setCategory: (state, action: PayloadAction<OfferCategory | "all">) => {
      state.filters.category = action.payload;
    },

    setSortBy: (
      state,
      action: PayloadAction<"newest" | "expiring" | "discount" | "popular">
    ) => {
      state.filters.sortBy = action.payload;
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.searchQuery = action.payload;
    },

    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
});

export const { setCategory, setSortBy, setSearchQuery, resetFilters } =
  offersSlice.actions;

// Base selector
const selectOffersState = (state: RootState) => state.offers;

// Memoized selectors
export const selectFilters = createSelector(
  [selectOffersState],
  (state) => state.filters
);

export const selectCategory = createSelector(
  [selectOffersState],
  (state) => state.filters.category
);

export const selectSortBy = createSelector(
  [selectOffersState],
  (state) => state.filters.sortBy
);

export const selectSearchQuery = createSelector(
  [selectOffersState],
  (state) => state.filters.searchQuery
);

// Composite selectors
export const selectHasFiltersApplied = createSelector(
  [selectFilters],
  (filters) =>
    filters.category !== "all" ||
    filters.sortBy !== "newest" ||
    filters.searchQuery !== ""
);

export default offersSlice.reducer;
